/**
 * TrackingConfigEditor — per-organization analytics / marketing pixel IDs
 * (Fase G.3). GA4, Meta Pixel and Google Tag Manager. These are public
 * values that end up in the storefront HTML; the frontends only load the
 * matching script after the visitor accepts cookies.
 */

import { useEffect, useState } from 'react';
import { organizationsApi } from '../../services/api';
import { useOrganizationStore } from '../../stores/organization';
import toast from 'react-hot-toast';
import { LoadingSpinner } from '../common';

const inputClass =
  'w-full rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500 font-mono';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

export default function TrackingConfigEditor() {
  const selectedOrgId = useOrganizationStore((s) => s.selectedOrg?._id);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const [ga4Id, setGa4Id] = useState('');
  const [metaPixelId, setMetaPixelId] = useState('');
  const [gtmId, setGtmId] = useState('');

  useEffect(() => {
    if (!selectedOrgId) return;
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgId]);

  const load = async () => {
    if (!selectedOrgId) return;
    setLoading(true);
    try {
      const res = await organizationsApi.getTrackingConfig(selectedOrgId);
      const d = res.data?.data || {};
      setGa4Id(d.ga4_id || '');
      setMetaPixelId(d.meta_pixel_id || '');
      setGtmId(d.gtm_id || '');
      setDirty(false);
    } catch {
      toast.error('Error al cargar la configuración de analítica');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedOrgId) return;
    setSaving(true);
    try {
      await organizationsApi.saveTrackingConfig(selectedOrgId, {
        ga4_id: ga4Id.trim(),
        meta_pixel_id: metaPixelId.trim(),
        gtm_id: gtmId.trim(),
      });
      toast.success('Configuración de analítica guardada');
      setDirty(false);
    } catch (e: unknown) {
      toast.error(
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al guardar'
      );
    } finally {
      setSaving(false);
    }
  };

  const mark = () => setDirty(true);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!selectedOrgId) {
    return (
      <div className="p-8 text-center text-gray-500">
        Selecciona una organización para configurar la analítica.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between bg-white rounded-xl border border-gray-200 px-6 py-3 flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Analítica y píxeles</h2>
          <p className="text-sm text-gray-500">
            IDs de seguimiento que se cargan en la web pública (tras consentimiento de cookies).
          </p>
        </div>
        <div className="flex items-center gap-3">
          {dirty && <span className="text-xs text-amber-600 font-medium">Cambios sin guardar</span>}
          <button
            onClick={handleSave}
            disabled={saving || !dirty}
            className="inline-flex items-center gap-2 px-4 py-2 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving && <LoadingSpinner size="sm" />}
            Guardar
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
        <div>
          <label className={labelClass}>Google Analytics 4 (Measurement ID)</label>
          <input
            type="text"
            value={ga4Id}
            onChange={(e) => { setGa4Id(e.target.value); mark(); }}
            className={inputClass}
            placeholder="G-XXXXXXXXXX"
            autoComplete="off"
          />
          <p className="text-xs text-gray-500 mt-1">Lo encuentras en GA4 → Administrar → Flujos de datos.</p>
        </div>

        <div>
          <label className={labelClass}>Meta Pixel ID</label>
          <input
            type="text"
            value={metaPixelId}
            onChange={(e) => { setMetaPixelId(e.target.value); mark(); }}
            className={inputClass}
            placeholder="123456789012345"
            autoComplete="off"
          />
          <p className="text-xs text-gray-500 mt-1">ID numérico del píxel en el Administrador de eventos de Meta.</p>
        </div>

        <div>
          <label className={labelClass}>Google Tag Manager (Container ID)</label>
          <input
            type="text"
            value={gtmId}
            onChange={(e) => { setGtmId(e.target.value); mark(); }}
            className={inputClass}
            placeholder="GTM-XXXXXXX"
            autoComplete="off"
          />
          <p className="text-xs text-gray-500 mt-1">
            Si usas GTM como contenedor, puedes gestionar GA4 y Meta desde ahí en vez de ponerlos aquí.
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
          Deja en blanco lo que no uses. La web pública solo carga cada script si su ID está
          relleno y el visitante ha aceptado las cookies.
        </div>
      </div>
    </div>
  );
}
